import React from 'react';
import { Card, Image } from 'antd';
import styled from 'styled-components';
import { Sport } from '../../../types/types';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';

interface SportsCardProps {
  sport: Sport;
  onDelete: (sport: Sport) => void;
  onEdit?: (sportModified: Sport) => void;
}

const SportsCard: React.FC<SportsCardProps> = ({ sport, onDelete, onEdit }) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleDelete = () => {
    onDelete(sport);
  };

  const handleEdit = () => {
    onEdit?.(sport);
  };

  return (
    <StyledCard
      title={
        <CardHeader>
          <TitleContainer>
            {sport.attachmentUrlDto?.attachmentUrl ? (
              <StyledImage
                src={sport.attachmentUrlDto?.attachmentUrl}
                alt={sport.sportName}
                preview={false}
              />
            ) : (
              <ImagePlaceholder>
                {sport.sportName.substring(0, 2).toUpperCase()}
              </ImagePlaceholder>
            )}
            <SportName title={sport.sportName}>{sport.sportName}</SportName>
          </TitleContainer>
          <ActionButtons>
            {onEdit && (
              <ActionButton
                $color={COLORS.skyBlue.primary}
                onClick={handleEdit}
              >
                Edit
              </ActionButton>
            )}
            <ActionButton
              $color={COLORS.skyBlue.primary}
              onClick={handleDelete}
            >
              Delete
            </ActionButton>
          </ActionButtons>
        </CardHeader>
      }
    >
      <Description>{sport.sportDescription}</Description>

      <AddedDate>Added on {formatDate(sport.createdOn)}</AddedDate>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 0.5px solid ${COLORS.grey.secondary};
  box-shadow: none;
  font-family: 'Roboto', sans-serif;

  .ant-card-head {
    border-bottom: none;
    padding: 16px 16px 0 16px;
    min-height: auto;
  }

  .ant-card-head-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .ant-card-head-title {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    font-size: ${TEXT_SIZES.MD}px;
    color: ${COLORS.black.primary};
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ant-card-body {
    padding: 12px 16px 16px 16px;
  }

  .ant-card-actions {
    display: none;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const ImagePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${COLORS.grey.primary};
  border: 1px dashed ${COLORS.grey.secondaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.XS}px;
  flex-shrink: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
`;

const ActionButton = styled.button<{ $color: string }>`
  background: none;
  border: none;
  color: ${(props) => props.$color};
  font-size: ${TEXT_SIZES.SM}px;
  cursor: pointer;
  padding: 0;
  font-family: 'Roboto', sans-serif;

  &:hover {
    opacity: 0.7;
  }
`;

const Description = styled.p`
  font-family: 'Roboto', sans-serif;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  font-size: ${TEXT_SIZES.SM}px;
  line-height: 17px;
  letter-spacing: 0;
  color: ${COLORS.black.primary};
  margin: 0 0 16px 0;
  min-height: 34px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const AddedDate = styled.p`
  font-family: 'Roboto', sans-serif;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  font-size: ${TEXT_SIZES.XS}px;
  line-height: 17px;
  letter-spacing: 0;
  color: ${COLORS.grey.secondary};
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid ${COLORS.grey.lightMedium};
`;

const StyledImage = styled(Image)`
  width: 42px !important;
  height: 42px !important;
  min-height: 42px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const SportName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default SportsCard;
